import { PageContainer, PageHeader, Spread } from '@/components/Atoms'
import { Card, CardContent } from '@/components/Card'
import { Filter } from '@/components/DataTable/FilterControls'
import { DateRange } from '@/components/DataTable/FilterControls/Filters/DateRangeFilter'
import useCustomerErpFilter from '@/hooks/filters/credit/useCustomerErpFilter'
import { reviewEventsSearchSchema, useReviewEvents } from '@/hooks/queries/credit/useReviewEvents'
import { useTablePagination } from '@/hooks/useTablePagination'
import { DateRangeControl, MultipleToggleListControl } from '@/lib/utils/filters'
import { REVIEW_EVENT_TYPE_KEYS, type ReviewEventTypeKey } from '@repo/constants'
import { createFileRoute } from '@tanstack/react-router'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ReviewEventsDataTable } from './-components/ReviewEventsDataTable'

export const Route = createFileRoute('/_auth/_modules/credit/credit-limit-reviews/review-events/')({
  component: RouteComponent,
  validateSearch: (search) => reviewEventsSearchSchema.parse(search),
})

function RouteComponent() {
  const { t } = useTranslation(['credit', 'common'])
  const navigate = Route.useNavigate()
  const search = Route.useSearch()

  const { pageSize, onPageSizeChange } = useTablePagination({ navigate, search })

  const [keyword, setKeyword] = useState<string>(search.keyword ?? '')

  const { erpFilter, erp, setErp } = useCustomerErpFilter(search.erp)

  const [typeKeys, setTypeKeys] = useState<ReviewEventTypeKey[]>(search.typeKey ?? [])
  const typeFilter = {
    control: MultipleToggleListControl,
    name: 'typeKey',
    title: t('reviewEvents.filters.type'),
    // Alphabetical by translated label, so the order adapts per language.
    options: REVIEW_EVENT_TYPE_KEYS.map((key) => ({
      value: key,
      label: t(`reviewEvents.type.${key}`),
    })).sort((a, b) => a.label.localeCompare(b.label)),
    value: typeKeys,
    onChange: setTypeKeys,
  }

  const [createdRange, setCreatedRange] = useState<DateRange>({
    from: search.createdFrom,
    to: search.createdTo,
  })
  const createdAtFilter = {
    control: DateRangeControl,
    name: 'createdAt',
    title: t('reviewEvents.filters.createdAt'),
    options: [],
    value: createdRange,
    onChange: setCreatedRange,
    fromParamName: 'createdFrom',
    toParamName: 'createdTo',
  }

  const filters = [typeFilter, erpFilter, createdAtFilter]

  const eventsQuery = useReviewEvents({ ...search, pageSize })

  const showResults = () => {
    navigate({
      search: {
        ...search,
        keyword: keyword || undefined,
        // `?.` because `resetFiltersPanel` can sync these back to undefined when absent from the URL.
        erp: erp?.length ? erp : undefined,
        typeKey: typeKeys?.length ? typeKeys : undefined,
        createdFrom: createdRange.from,
        createdTo: createdRange.to,
        page: 1,
      },
    })
  }

  const resetAllFilters = () => {
    setKeyword('')
    setErp([])
    setTypeKeys([])
    setCreatedRange({ from: undefined, to: undefined })

    navigate({
      search: {
        ...search,
        keyword: undefined,
        erp: undefined,
        typeKey: undefined,
        createdFrom: undefined,
        createdTo: undefined,
        page: 1,
      },
    })
  }

  return (
    <PageContainer>
      <Spread align="end">
        <PageHeader>{t('reviewEvents.pageHeader')}</PageHeader>
      </Spread>

      <Card>
        <CardContent className="pt-6">
          <ReviewEventsDataTable
            filters={filters as Filter<unknown>[]}
            search={{
              value: keyword,
              setValue: setKeyword,
              placeholder: t('reviewEvents.searchPlaceholder'),
            }}
            pageSize={pageSize}
            onPageSizeChange={onPageSizeChange}
            eventsQuery={eventsQuery}
            onShowResults={showResults}
            onResetAllFilters={resetAllFilters}
          />
        </CardContent>
      </Card>
    </PageContainer>
  )
}
